/**
 * Renders fog-of-war as instanced hex plates with multiple
 * parallax layers, pre-baked FBM noise, and volumetric lighting.
 * All layers share one VAO — only uniforms differ per draw.
 */
import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { FogOverlayConfig } from '../config/FogConfig';
import {
  type LayerState, buildLayerStates, createNoiseTexture,
  buildFogHexGeometry, buildHexIndices,
} from './FogGeometryHelpers';

import vertSrc from '../shaders/fog.vert.glsl?raw';
import fragSrc from '../shaders/fog.frag.glsl?raw';

export class FogRenderer implements Renderable {
  private _gl: WebGL2RenderingContext;
  private _shader: ShaderProgram;
  private _mesh: InstancedMesh;
  private _layers: LayerState[];
  private _hexRadius: number;
  private _overlay: FogOverlayConfig;
  private _instanceCount = 0;
  private _suppressed = false;
  private _noiseTex: WebGLTexture;

  constructor(gl: WebGL2RenderingContext, hexRadius: number, overlay: FogOverlayConfig) {
    this._gl = gl;
    this._hexRadius = hexRadius;
    this._overlay = overlay;
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);
    this._noiseTex = createNoiseTexture(gl, 256);

    this._mesh = new InstancedMesh(
      gl,
      this._shader,
      buildFogHexGeometry(),
      [{ name: 'a_corner', size: 2, divisor: 0 }],
      [
        { name: 'a_offset', size: 2, divisor: 1 },
        { name: 'a_adjA', size: 3, divisor: 1 },
        { name: 'a_adjB', size: 3, divisor: 1 },
      ],
      buildHexIndices(),
    );

    this._layers = buildLayerStates(overlay);
  }

  get instanceCount(): number { return this._instanceCount; }
  set suppressed(v: boolean) { this._suppressed = v; }

  updateInstances(data: Float32Array, count: number): void {
    this._instanceCount = count;
    this._mesh.updateInstances(data, count);
  }

  advanceTime(dt: number): void {
    for (let i = 0; i < this._layers.length; i++) {
      this._layers[i].time += dt * this._layers[i].timeScale;
    }
  }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._suppressed || this._instanceCount === 0) return;

    gl.depthMask(false);
    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setFloat('u_hexRadius', this._hexRadius);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._noiseTex);
    this._shader.setInt('u_noise', 0);

    const ov = this._overlay;
    this._shader.setFloat('u_cloudDensity', ov.cloudDensity);
    this._shader.setFloat('u_cloudContrast', ov.cloudContrast);
    this._shader.setFloat('u_cloudFlow', ov.cloudFlowSpeed);
    this._shader.setFloat('u_baseOpacity', ov.cloudBaseOpacity);
    this._shader.setFloat('u_edgeFadeStrength', ov.edgeFadeStrength);
    this._shader.setFloat('u_volumetricStyle', ov.volumetric.enabled ? 1.0 : 0.0);
    this._shader.setVec3('u_lightDir',
      ov.volumetric.lightDir[0], ov.volumetric.lightDir[1], ov.volumetric.lightDir[2]);
    this._shader.setFloat('u_highlightStrength', ov.volumetric.highlightStrength);
    this._shader.setFloat('u_shadowStrength', ov.volumetric.shadowStrength);
    this._shader.setVec3('u_topTint',
      ov.volumetric.topTint[0], ov.volumetric.topTint[1], ov.volumetric.topTint[2]);
    this._shader.setVec3('u_bottomTint',
      ov.volumetric.bottomTint[0], ov.volumetric.bottomTint[1], ov.volumetric.bottomTint[2]);

    for (let i = 0; i < this._layers.length; i++) {
      const l = this._layers[i];
      this._shader.setFloat('u_time', l.time);
      this._shader.setFloat('u_baseY', l.baseY);
      this._shader.setFloat('u_noiseFreq', l.noiseFreq);
      this._shader.setFloat('u_noiseAmp', l.noiseAmp);
      this._shader.setFloat('u_cloudDetail', l.cloudDetail);
      this._shader.setFloat('u_layerParallax', l.layerParallax);
      this._shader.setFloat('u_parallaxNormalY', l.parallaxNormalY);
      this._shader.setVec3('u_color', l.colorR, l.colorG, l.colorB);
      this._shader.setFloat('u_alpha', l.alpha);
      this._mesh.draw();
    }

    gl.depthMask(true);
  }

  dispose(): void {
    this._shader.dispose();
    this._mesh.dispose();
    this._gl.deleteTexture(this._noiseTex);
  }
}
